import { SUB_STATUS_ID } from '@/helpers/constants';

/**
 * Applies one CustomMultiSelect event to the current sub-status selection.
 *
 * Extracted from SubStatus.jsx so the rules are testable on their own — in particular the
 * blocker behavior, where MX/CX/Attention Needed persist until a scheduler explicitly picks
 * Inactive, which clears the whole set.
 *
 * @param {Array<{id: number}>} current currently selected sub-statuses
 * @param {{operation: string, items: Array<{id: number}>}} event
 * @param {Array<{id: number}>} allSubStatuses master list, used to look up companion statuses
 * @returns {Array<{id: number}>} the new selection (never the same array instance)
 */
export const applySubStatusChange = (current, event, allSubStatuses) => {
  const newItem = event?.items?.[0];
  const operation = event?.operation;
  let tempFilteredObjects = Array.isArray(current) ? [ ...current ] : [];
  const catalog = Array.isArray(allSubStatuses) ? allSubStatuses : [];

  // Adds a companion status from the master list. Skips silently when the master data has not
  // loaded or does not carry that id — pushing the undefined `find` returned would put a hole in
  // the selection and break rendering downstream.
  const pushFromCatalog = (id) => {
    const item = catalog.find((entry) => entry?.id === id);
    if (item) {
      tempFilteredObjects.push(item);
    }
  };

  if (operation === 'toggle' && newItem) {
    const id = newItem.id;
    const itemIndex = tempFilteredObjects.findIndex((obj) => obj.id === id);
    if (itemIndex !== -1) {
      tempFilteredObjects.splice(itemIndex, 1);
    } else {
      const inactiveIndex = tempFilteredObjects.findIndex((obj) => obj.id === SUB_STATUS_ID.INACTIVE);
      switch (id) {
        case SUB_STATUS_ID.ACTIVE:
          if (inactiveIndex !== -1) {
            tempFilteredObjects.splice(inactiveIndex, 1);
          }
          tempFilteredObjects.push(newItem);
          break;
        case SUB_STATUS_ID.INACTIVE:
          tempFilteredObjects = [];
          tempFilteredObjects.push(newItem);
          break;
        // MX, CX and Attention Needed (SKYL-2777) are blockers: they force the leg Active and
        // into Queue, and persist until a scheduler explicitly picks Inactive.
        case SUB_STATUS_ID.MX:
        case SUB_STATUS_ID.CX:
        case SUB_STATUS_ID.ATTENTION_NEEDED:
          tempFilteredObjects.push(newItem);
          if (!tempFilteredObjects.some((obj) => obj.id === SUB_STATUS_ID.ACTIVE)) {
            pushFromCatalog(SUB_STATUS_ID.ACTIVE);
            if (inactiveIndex !== -1) {
              tempFilteredObjects.splice(inactiveIndex, 1);
            }
          }
          if (!tempFilteredObjects.some((obj) => obj.id === SUB_STATUS_ID.QUEUE)) {
            pushFromCatalog(SUB_STATUS_ID.QUEUE);
          }
          break;
        case SUB_STATUS_ID.MATCHUP:
        case SUB_STATUS_ID.QUEUE:
          pushFromCatalog(id);
          break;
        default:
          break;
      }
    }
  } else if (operation === 'delete' && newItem) {
    const deleteIndex = tempFilteredObjects.findIndex((obj) => obj.id === newItem.id);
    // Guarded: splice(-1, 1) would drop the last selected status when the item is not there.
    if (deleteIndex !== -1) {
      tempFilteredObjects.splice(deleteIndex, 1);
    }
  } else {
    tempFilteredObjects.length = 0;
  }

  return tempFilteredObjects;
};

/**
 * Builds the sub-status option list for the dropdowns from the master data.
 *
 * Drops New (id 7), which is a system-assigned status schedulers never pick. Everything else
 * keeps the order the API sent: getAllSubStatus() sorts by scheduler_sub_status.display_sequence,
 * which is where the dropdown order is decided — including Attention Needed sitting directly
 * above Inactive. Do not reorder here; that would put the display order in two places.
 *
 * @param {Array<{id: number}>} masterData all-scheduler-sub-status master response
 * @returns {Array<{id: number}>} options in display order
 */
export const buildSubStatusOptions = (masterData) =>
  Array.isArray(masterData) ? masterData.filter((f) => f?.id !== SUB_STATUS_ID.NEW) : [];
